/**
 * `Clickable` — high-performance press wrapper. Children are the only content (single root recommended).
 * Motion, blur, and tints run on the UI thread via Reanimated; only `onPress` / haptics use `runOnJS`.
 *
 * **Stack (same idea as `MainScreen` glass cards):** shadow/elevation, when present, sits on an **outer** host
 * that does not clip. Blur, press wash, and error use **separate** absolutely positioned layers with matching
 * corner radii and `overflow: 'hidden'` only on those layers — so rounded geometry stays aligned and shadows
 * are never clipped.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform, Pressable, StyleProp, StyleSheet, View, type AccessibilityRole, type AccessibilityState, type ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  clamp,
  Extrapolation,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import type { HapticType } from '../../shared/utils/haptics';
import { triggerHaptic } from '../../shared/utils/haptics';
import type { ThemeColors } from '../../shared/theme/store';
import { appTheme, useThemeColors, useThemeStore } from '../../shared/theme';
import { blurViewWrapperProps, androidStripElevationSeams } from '../../shared/theme/sharedStyles';
import { hexToRgba } from '../../shared/theme';

export type { HapticType };

/** Shared with previous press presets — `MainScreen` list rows, `AuthChip`, etc. */
export const PRESS_SPRING = { stiffness: 200, damping: 24 };
/** Default scale at full press for generic targets. */
export const PRESS_SCALE = 0.972;
export const PRESS_PRESETS = {
  button: { spring: PRESS_SPRING, scale: PRESS_SCALE },
  listRow: {
    spring: { stiffness: 150, damping: 20 },
    scale: 0.987,
  },
} as const;

const MAX_GESTURE_DISTANCE = 25;
const TAP_MAX_DURATION_MS = 120_000;

const SHADOW_STYLE_KEYS: (keyof ViewStyle)[] = [
  'elevation',
  'shadowColor',
  'shadowOffset',
  'shadowOpacity',
  'shadowRadius',
];

/** Layout (and only layout) duplicated onto the shadow host; margins live only on the host. */
const LAYOUT_ON_SHADOW_HOST: (keyof ViewStyle)[] = [
  'alignSelf',
  'alignContent',
  'alignItems',
  'justifyContent',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'flexDirection',
  'flexWrap',
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginStart',
  'marginEnd',
  'marginVertical',
  'marginHorizontal',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'display',
  'direction',
  'start',
  'end',
  'gap',
  'transform',
  'transformOrigin',
  'aspectRatio',
];

const MARGIN_PROP_KEYS: (keyof ViewStyle)[] = [
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginStart',
  'marginEnd',
  'marginVertical',
  'marginHorizontal',
];

export type ClickableColorVariant = 'primary' | 'secondary' | 'tertiary' | 'outline' | 'ghost' | 'link' | 'danger';

export type ClickableBlurType =
  | 'light'
  | 'dark'
  | 'xlight'
  | 'regular'
  | 'prominent'
  | 'material'
  | 'materialLight'
  | 'materialDark'
  | 'thinMaterial'
  | 'thickMaterial'
  | 'ultraThinMaterial'
  | 'chromeMaterial'
  | 'extraDark';

export interface HitSlopRect {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

export type ClickableContentBorderRadii = {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
};

type CornerRadii = ClickableContentBorderRadii;

export interface ClickableProps {
  children: React.ReactNode;
  onPress?: () => void;
  onPressIn?: () => void;
  style?: StyleProp<ViewStyle>;
  /** Wraps `children` — use for flex alignment when the outer `style` is the hit area. */
  contentStyle?: StyleProp<ViewStyle>;
  /** Uniform corner radius for blur, press wash, and error (when `style` has no / wrong radius, e.g. `flex: 1` only). */
  contentBorderRadius?: number;
  /**
   * Per-corner underlay radii — when set, used for blur, press, error, and shadow host shape (overrides
   * `contentBorderRadius` and radii on `style`).
   */
  contentBorderRadii?: ClickableContentBorderRadii;
  disabled?: boolean;
  /** Validation / API error: visible ring + semantic `accessibilityState` (text contrast stays in `children` / theme). */
  error?: boolean;
  /** Default visual language for press wash + borders (tied to `ThemeColors`). */
  colorVariant?: ClickableColorVariant;
  showBlur?: boolean;
  /** Add a second frosted tint on top of blur (theme-aware). */
  tintedBlur?: boolean;
  tintedBlurOverlayColor?: string;
  blurType?: ClickableBlurType;
  blurAmount?: number;
  /** When > 0 and no radii from props/flatten, forces a uniform underlay radius (legacy escape hatch). */
  blurBorderRadius?: number;
  haptics?: boolean;
  hapticType?: HapticType;
  /** Nudge haptic on tap when `disabled` (e.g. explain why CTA is inactive). */
  hapticOnDisabledPress?: boolean;
  /**
   * When `false`, tap still calls `onPress` but there is no scale or press-wash (backdrops, overlay dismissers).
   * Replaces the old `isAnimated={false}`.
   */
  pressAnimated?: boolean;
  onLongPress?: () => void;
  delayLongPress?: number;
  pressScale?: number;
  pressSpring?: { stiffness: number; damping: number };
  hitSlop?: number | HitSlopRect;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityState?: AccessibilityState;
  testID?: string;
  useNativePressable?: boolean;
}

const DEFAULT_SPRING = { stiffness: 360, damping: 28, mass: 0.4 };
const RELEASE_SPRING = { stiffness: 420, damping: 32, mass: 0.42 };
const PRESS_SCALE_DEFAULT = 0.972;

const ZERO_RADII: CornerRadii = { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 };

function toNum(v: unknown, fallback: number): number {
  if (v == null || v === 'auto') {
    return fallback;
  }
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
}

function radiiFromFlat(flat: ViewStyle | null | undefined): CornerRadii {
  if (flat == null) {
    return { ...ZERO_RADII };
  }
  const br = flat.borderRadius;
  const u = toNum(br, 0);
  if (u > 0) {
    return { topLeft: u, topRight: u, bottomLeft: u, bottomRight: u };
  }
  return {
    topLeft: toNum(flat.borderTopLeftRadius, 0),
    topRight: toNum(flat.borderTopRightRadius, 0),
    bottomLeft: toNum(flat.borderBottomLeftRadius, 0),
    bottomRight: toNum(flat.borderBottomRightRadius, 0),
  };
}

function allCornersEqual(m: ClickableContentBorderRadii): boolean {
  return m.topLeft === m.topRight && m.topLeft === m.bottomLeft && m.topLeft === m.bottomRight;
}

function resolveMaskRadii(
  surfaceFlat: ViewStyle,
  contentBorderRadius: number | undefined,
  contentBorderRadii: ClickableContentBorderRadii | undefined,
  blurBorderRadius: number
): CornerRadii {
  if (contentBorderRadii != null) {
    return { ...contentBorderRadii };
  }
  if (contentBorderRadius != null && contentBorderRadius > 0) {
    const n = contentBorderRadius;
    return { topLeft: n, topRight: n, bottomLeft: n, bottomRight: n };
  }
  if (blurBorderRadius > 0) {
    const b = blurBorderRadius;
    return { topLeft: b, topRight: b, bottomLeft: b, bottomRight: b };
  }
  return radiiFromFlat(surfaceFlat);
}

function viewStyleFromCornerRadii(r: CornerRadii): ViewStyle {
  if (allCornersEqual(r)) {
    if (r.topLeft <= 0) {
      return {};
    }
    return { borderRadius: r.topLeft };
  }
  if (r.topLeft + r.topRight + r.bottomLeft + r.bottomRight <= 0) {
    return {};
  }
  return {
    borderTopLeftRadius: r.topLeft,
    borderTopRightRadius: r.topRight,
    borderBottomLeftRadius: r.bottomLeft,
    borderBottomRightRadius: r.bottomRight,
  };
}

function pickLayoutForShadowHost(rest: ViewStyle): ViewStyle {
  const o: ViewStyle = {};
  for (const k of LAYOUT_ON_SHADOW_HOST) {
    if (k in rest && (rest as Record<string, unknown>)[k] !== undefined) {
      (o as Record<string, unknown>)[k] = (rest as Record<string, unknown>)[k];
    }
  }
  return o;
}

function stripMarginProps(rest: ViewStyle): ViewStyle {
  const out: ViewStyle = { ...rest };
  for (const k of MARGIN_PROP_KEYS) {
    delete (out as Record<string, unknown>)[k as string];
  }
  return out;
}

function splitShadowFromRest(restIn: ViewStyle): { shadow: ViewStyle; rest: ViewStyle } {
  const shadow: ViewStyle = {};
  const rest: ViewStyle = { ...restIn };
  for (const k of SHADOW_STYLE_KEYS) {
    if (k in rest && (rest as Record<string, unknown>)[k] !== undefined) {
      (shadow as Record<string, unknown>)[k] = (rest as Record<string, unknown>)[k];
      delete (rest as Record<string, unknown>)[k as string];
    }
  }
  if ('overflow' in rest) {
    delete (rest as Record<string, unknown>).overflow;
  }
  return { shadow, rest };
}

function isMeaningfulShadow(s: ViewStyle): boolean {
  const e = s.elevation;
  if (e != null && Number(e) > 0) {
    return true;
  }
  const so = s.shadowOpacity;
  if (so != null && Number(so) > 0) {
    return true;
  }
  if (s.shadowColor != null) {
    return true;
  }
  if (s.shadowOffset != null) {
    return true;
  }
  const sr = s.shadowRadius;
  if (sr != null && Number(sr) > 0) {
    return true;
  }
  return false;
}

function toHitSlop(hitSlop: number | HitSlopRect | undefined): { top: number; left: number; right: number; bottom: number } | undefined {
  if (hitSlop == null) {
    return undefined;
  }
  if (typeof hitSlop === 'number') {
    return { top: hitSlop, left: hitSlop, right: hitSlop, bottom: hitSlop };
  }
  return {
    top: hitSlop.top ?? 0,
    left: hitSlop.left ?? 0,
    right: hitSlop.right ?? 0,
    bottom: hitSlop.bottom ?? 0,
  };
}

function androidPressableRipple(variant: ClickableColorVariant, c: ThemeColors) {
  switch (variant) {
    case 'primary':
      return { color: hexToRgba(c.onPrimary ?? c.white, 0.22) };
    case 'outline':
      return { color: hexToRgba(c.primary, 0.14) };
    case 'link':
      return { color: hexToRgba(c.primary, 0.1) };
    case 'danger':
      return { color: hexToRgba(c.error, 0.14) };
    default:
      return { color: hexToRgba(c.text, 0.08) };
  }
}

function pressWashColor(variant: ClickableColorVariant, c: ThemeColors): { rest: string; down: string } {
  const primaryWash = c.primary + '1A';
  const secondaryWash = c.secondary + '1A';
  const tertiaryWash = c.tertiary + '16';
  const textWash = c.text + '0D';
  const dangerWash = c.error + '22';
  const outlineWash = c.border + '33';
  switch (variant) {
    case 'primary':
      return { rest: 'transparent', down: c.primaryPressed + '2E' };
    case 'secondary':
      return { rest: 'transparent', down: secondaryWash };
    case 'tertiary':
      return { rest: 'transparent', down: tertiaryWash };
    case 'outline':
      return { rest: 'transparent', down: outlineWash };
    case 'ghost':
      return { rest: 'transparent', down: textWash };
    case 'link':
      return { rest: 'transparent', down: primaryWash };
    case 'danger':
      return { rest: 'transparent', down: dangerWash };
    default:
      return { rest: 'transparent', down: textWash };
  }
}

function blurTintOverPrimary(c: ThemeColors): string {
  return c.primary + '1F';
}

function errorBorder(c: ThemeColors) {
  return c.error;
}

/**
 * @see `SolidCtaClickable`, `MainScreen` action rows, credential sheets — single app-wide `Clickable`.
 */
export function Clickable({
  children,
  onPress,
  onPressIn,
  style,
  contentStyle,
  contentBorderRadius: contentBorderRadiusProp,
  contentBorderRadii,
  disabled = false,
  error = false,
  colorVariant = 'ghost',
  showBlur = false,
  tintedBlur = false,
  tintedBlurOverlayColor,
  blurType = 'xlight',
  blurAmount = 5,
  blurBorderRadius = 0,
  haptics = false,
  hapticType = 'soft',
  hapticOnDisabledPress = true,
  pressAnimated = true,
  onLongPress,
  delayLongPress = 500,
  pressScale = PRESS_SCALE_DEFAULT,
  pressSpring = DEFAULT_SPRING,
  hitSlop,
  accessibilityLabel,
  accessibilityRole = 'button',
  accessibilityState: accessibilityStateProp,
  testID,
  useNativePressable = false,
}: ClickableProps) {
  // Web: Pressable is reliable; gesture-handler worklets are stubbed on PWA.
  // Native keeps the caller's choice (Android MainScreen forces Pressable; iOS uses gestures).
  const resolve_native_pressable = useNativePressable || Platform.OS === 'web';
  const colors = useThemeColors() ?? appTheme.colors;
  const appearance = useThemeStore(s => s.appearance);
  const is_dark = appearance === 'dark';
  const wash = useMemo(() => pressWashColor(colorVariant, colors), [colorVariant, colors]);
  const blurTint = useMemo(() => blurTintOverPrimary(colors), [colors]);
  const errBorder = useMemo(() => errorBorder(colors), [colors]);

  const press_depth = useSharedValue(0);
  const disabled_sv = useSharedValue(disabled ? 1 : 0);
  const haptics_on = useSharedValue(haptics ? 1 : 0);
  const haptics_on_disabled = useSharedValue(hapticOnDisabledPress ? 1 : 0);
  const reduced = useReducedMotion();
  const motion = useSharedValue(1);
  const press_fx = useSharedValue(pressAnimated ? 1 : 0);
  const hit_slop_insets = useMemo(() => toHitSlop(hitSlop), [hitSlop]);

  const flat = useMemo(() => (StyleSheet.flatten(style) as ViewStyle | null | undefined) ?? ({} as ViewStyle), [style]);
  const { shadow: shadow_block, rest: after_shadow } = useMemo(() => splitShadowFromRest(flat), [flat]);
  const has_shadow_host = useMemo(() => isMeaningfulShadow(shadow_block), [shadow_block]);

  const host_layout = useMemo(() => pickLayoutForShadowHost(after_shadow), [after_shadow]);
  const inner_surface = useMemo(
    () => (has_shadow_host ? stripMarginProps(after_shadow) : { ...after_shadow }),
    [after_shadow, has_shadow_host]
  );

  const mask_radii = useMemo(
    () => resolveMaskRadii(after_shadow, contentBorderRadiusProp, contentBorderRadii, blurBorderRadius),
    [after_shadow, contentBorderRadiusProp, contentBorderRadii, blurBorderRadius]
  );
  const corner = useMemo(() => viewStyleFromCornerRadii(mask_radii), [mask_radii]);

  useEffect(() => {
    disabled_sv.value = disabled ? 1 : 0;
  }, [disabled, disabled_sv]);
  useEffect(() => {
    haptics_on.value = haptics ? 1 : 0;
  }, [haptics, haptics_on]);
  useEffect(() => {
    haptics_on_disabled.value = hapticOnDisabledPress ? 1 : 0;
  }, [hapticOnDisabledPress, haptics_on_disabled]);
  useEffect(() => {
    const v = reduced ? 0 : 1;
    motion.value = v;
  }, [reduced, motion]);
  useEffect(() => {
    press_fx.value = pressAnimated ? 1 : 0;
  }, [pressAnimated, press_fx]);

  const haptic_ref = useRef({ hapticType });
  haptic_ref.current = { hapticType };

  const fireHaptic = useCallback((kind: 'success' | 'disabled') => {
    if (kind === 'success' && haptics) {
      triggerHaptic(haptic_ref.current.hapticType);
    } else if (kind === 'disabled' && hapticOnDisabledPress) {
      triggerHaptic('rigid');
    }
  }, [hapticOnDisabledPress, haptics]);

  const on_js_press = useCallback(() => {
    onPress?.();
  }, [onPress]);

  const on_js_press_in = useCallback(() => {
    onPressIn?.();
  }, [onPressIn]);

  const on_js_long = useCallback(() => {
    onLongPress?.();
  }, [onLongPress]);

  const build_tap = useCallback(() => {
    const spring_in = { ...pressSpring, mass: (pressSpring as { mass?: number }).mass ?? 0.4 };

    let g = Gesture.Tap()
      .maxDuration(TAP_MAX_DURATION_MS)
      .maxDistance(MAX_GESTURE_DISTANCE)
      .onBegin(() => {
        'worklet';
        if (false) {
          runOnJS(console.log)('[Clickable gesture onBegin]', { testID, accessibilityLabel });
        }
        if (disabled_sv.value > 0.5) {
          if (false) {
            runOnJS(console.log)('[Clickable gesture blocked disabled]', { testID, accessibilityLabel });
          }
          return;
        }
        if (onPressIn != null) {
          runOnJS(on_js_press_in)();
        }
        if (press_fx.value > 0.5) {
          press_depth.value = withSpring(1, spring_in);
        }
      })
      .onEnd((_, success) => {
        'worklet';
        if (false) {
          runOnJS(console.log)('[Clickable gesture onEnd]', { testID, accessibilityLabel, success });
        }
        if (!success) {
          return;
        }
        if (disabled_sv.value > 0.5) {
          if (false) {
            runOnJS(console.log)('[Clickable gesture blocked disabled]', { testID, accessibilityLabel });
          }
          if (haptics_on_disabled.value) {
            runOnJS(fireHaptic)('disabled');
          }
          return;
        }
        if (haptics_on.value) {
          runOnJS(fireHaptic)('success');
        }
        runOnJS(on_js_press)();
      })
      .onFinalize(() => {
        'worklet';
        if (false) {
          runOnJS(console.log)('[Clickable gesture onFinalize]', { testID, accessibilityLabel });
        }
        if (press_fx.value > 0.5) {
          press_depth.value = withSpring(0, RELEASE_SPRING);
        } else {
          press_depth.value = 0;
        }
      });
    g = g.enabled(!!onPress);
    if (hit_slop_insets) {
      g = g.hitSlop(hit_slop_insets);
    }
    return g;
  }, [onPress, onPressIn, hit_slop_insets, pressSpring, on_js_press, on_js_press_in, fireHaptic, press_fx, disabled_sv, haptics_on, haptics_on_disabled]);

  const main_gesture = useMemo(() => {
    const build_long = () => {
      let lg = Gesture.LongPress()
        .minDuration(delayLongPress)
        .maxDistance(MAX_GESTURE_DISTANCE)
        .onStart(() => {
          'worklet';
          if (disabled_sv.value > 0.5) {
            return;
          }
          runOnJS(on_js_long)();
        });
      if (onPress == null) {
        lg = lg.enabled(true);
      }
      if (hit_slop_insets) {
        lg = lg.hitSlop(hit_slop_insets);
      }
      return lg;
    };
    if (onLongPress != null && onPress == null) {
      return build_long();
    }
    const tapG = build_tap();
    if (onLongPress != null) {
      return Gesture.Race(build_long(), tapG);
    }
    return tapG;
  }, [build_tap, onLongPress, onPress, on_js_long, delayLongPress, hit_slop_insets]);

  const a_style = useAnimatedStyle(() => {
    'worklet';
    const m = motion.value;
    const d = clamp(press_depth.value, 0, 1);
    const edge = 1 + 0.12 * d * m;
    const fx = press_fx.value;
    const s = 1 + (pressScale - 1) * d * edge * fx;
    const off = disabled_sv.value > 0.5;
    return {
      transform: [{ scale: s }],
      opacity: off
        ? 0.5
        : fx < 0.5
          ? 1
          : interpolate(d, [0, 1], [1, 0.99], Extrapolation.CLAMP),
    };
  }, [pressScale]);

  /** Press tint without `interpolateColor` (not reliably available in worklets with RN 4 + worklets plugin). `pressWashColor` uses transparent rest + tinted `down`; fading `down` in matches the prior blend. */
  const press_wash = useAnimatedStyle(() => {
    'worklet';
    const d = clamp(press_depth.value, 0, 1);
    const fx = press_fx.value;
    const t = d * fx;
    return {
      backgroundColor: wash.down,
      opacity: interpolate(t, [0, 1], [0, 1], Extrapolation.CLAMP),
    };
  }, [wash]);

  const outer_view_style: ViewStyle[] = useMemo(() => {
    if (!has_shadow_host) {
      return [];
    }
    const shadow_layer = Platform.OS === 'android' ? androidStripElevationSeams() : shadow_block;
    return [host_layout, shadow_layer, corner, { backgroundColor: 'transparent' } as ViewStyle];
  }, [has_shadow_host, host_layout, shadow_block, corner]);

  const accessibility_state: AccessibilityState = {
    ...accessibilityStateProp,
    disabled: disabled || !!accessibilityStateProp?.disabled,
  };

  const underlay_clipped = [StyleSheet.absoluteFill, corner, blurViewWrapperProps(), { zIndex: 0 } as ViewStyle];

  const surface = (
    <Animated.View
      testID={testID}
      accessible
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      accessibilityState={accessibility_state}
      style={[inner_surface, a_style, styles.surfaceRoot, has_shadow_host && styles.fillShadowHost]}
    >
      <Animated.View
        style={underlay_clipped}
        pointerEvents="none"
        importantForAccessibility="no-hide-descendants"
        accessibilityElementsHidden
        collapsable={false}>
        {showBlur ? (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: colors.surface }]} />
        ) : null}
        {showBlur && tintedBlur ? (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: tintedBlurOverlayColor ?? blurTint }]} />
        ) : null}
        <Animated.View style={[StyleSheet.absoluteFill, press_wash]} />
      </Animated.View>
      {error ? (
        <View
          pointerEvents="none"
          importantForAccessibility="no-hide-descendants"
          accessibilityElementsHidden
          style={[
            StyleSheet.absoluteFill,
            corner,
            { zIndex: 1, borderWidth: 2, borderColor: errBorder, backgroundColor: 'transparent' },
          ]}
        />
      ) : null}
      <View style={[styles.content, { zIndex: 2 }, contentStyle]} pointerEvents="box-none">
        {children}
      </View>
    </Animated.View>
  );

  if (resolve_native_pressable) {
    const android_ripple = Platform.OS === 'android' ? androidPressableRipple(colorVariant, colors) : undefined;
    const has_corner_mask =
      mask_radii.topLeft + mask_radii.topRight + mask_radii.bottomLeft + mask_radii.bottomRight > 0;

    return (
      <Pressable
        testID={testID}
        disabled={disabled || !onPress}
        hitSlop={hitSlop}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        accessibilityState={accessibility_state}
        android_ripple={android_ripple}
        onPress={() => {
          onPress?.();
        }}
        onPressIn={onPressIn}
        onLongPress={onLongPress}
        delayLongPress={delayLongPress}
        style={({ pressed }) => [
          has_shadow_host ? outer_view_style : null,
          inner_surface,
          corner,
          has_corner_mask ? ({ overflow: 'hidden' } as ViewStyle) : null,
          disabled && { opacity: 0.5 },
          Platform.OS === 'ios' && pressed && !disabled && { opacity: 0.85 },
          Platform.OS === 'android' && pressed && !disabled && pressAnimated && { transform: [{ scale: 0.985 }] },
        ]}
      >
        <View style={[styles.content, { zIndex: 2 }, contentStyle]} pointerEvents="none">
          {children}
        </View>
      </Pressable>
    );
  }

  if (has_shadow_host) {
    return (
      <GestureDetector gesture={main_gesture}>
        <View style={outer_view_style} collapsable={false}>
          {surface}
        </View>
      </GestureDetector>
    );
  }

  return (
    <GestureDetector gesture={main_gesture}>
      <View collapsable={false}>{surface}</View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  surfaceRoot: {
    minWidth: 0,
  },
  /** Inner surface fills the non-clipping shadow host. */
  fillShadowHost: {
    flex: 1,
    minWidth: 0,
    width: '100%',
  },
  content: {
    minWidth: 0,
    width: '100%',
  },
});

export default Clickable;
